import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import "./importInventoryModal.scss";
import Loader from "../../common/Loader";
import { ApiGet, ApiPost } from "../../../helpers/API/ApiData";
import Success from "../../Toaster/Success/Success";
import {
  buildHeaderMap,
  canonicalizeDisplayName,
  computeFinalProductName,
  extractApiErrorMessage,
  normalizeName,
  normalizeLooseName,
  validateRow,
} from "../../../utils/inventoryImport.util";

export default function ImportInventoryModal({ toggle, companyId }) {
  const fileRef = useRef(null);
  const brandMapRef = useRef({});
  const categoryMapRef = useRef({});
  const brandInFlightRef = useRef({}); // key -> Promise resolving to {id,name}
  const categoryInFlightRef = useRef({}); // key -> Promise resolving to {id,name}
  const closeTimeoutRef = useRef(null);
  const toastTimeoutRef = useRef(null);
  const mountedRef = useRef(true);
  const cancelImportRef = useRef(false);
  const logsRef = useRef([]);
  const flushTimerRef = useRef(null);
  const countersRef = useRef({
    processed: 0,
    success: 0,
    failed: 0,
    skipped: 0,
  });
  const progressFlushTimerRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [toastmsg, setToastmsg] = useState("");
  const [er, setEr] = useState("");

  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState([]);
  const [headerMap, setHeaderMap] = useState({});
  const [detectedHeaders, setDetectedHeaders] = useState([]);

  const [autoCreateMissing, setAutoCreateMissing] = useState(true);
  const [prefixBrandToName, setPrefixBrandToName] = useState(true);

  const [brandMap, setBrandMap] = useState({}); // normalizedName -> { id, name }
  const [categoryMap, setCategoryMap] = useState({}); // normalizedName -> { id, name }
  const [apiPaths, setApiPaths] = useState({
    brandList: null,
    brandCreate: null,
    categoryList: null,
    categoryCreate: null,
    productCreate: "product",
  });

  const [progress, setProgress] = useState({
    total: 0,
    processed: 0,
    success: 0,
    failed: 0,
    skipped: 0,
    running: false,
  });
  const [logs, setLogs] = useState([]);

  const totalRows = rows?.length || 0;

  const canImport = useMemo(() => {
    return (
      !!companyId &&
      totalRows > 0 &&
      !!headerMap?.productName &&
      !!headerMap?.brand &&
      !!headerMap?.category &&
      !!headerMap?.price &&
      !!headerMap?.quantity &&
      !progress.running
    );
  }, [companyId, headerMap, progress.running, totalRows]);

  useEffect(() => {
    let mounted = true;
    const fetchLists = async () => {
      if (!companyId) return;
      setIsLoading(true);
      try {
        const tryGetFirst = async (paths) => {
          let lastErr;
          for (const p of paths) {
            try {
              // eslint-disable-next-line no-await-in-loop
              const res = await ApiGet(p);
              return { path: p, res };
            } catch (e) {
              lastErr = e;
            }
          }
          throw lastErr || new Error("No working endpoint found");
        };

        const brandListCandidates = [
          `ibrand/company/${companyId}`,
          `ibrand/${companyId}`,
          `ibrand?companyId=${companyId}`,
          `ibrand`,
          `brand/company/${companyId}`,
          `brands/company/${companyId}`,
          `inventory/brand/company/${companyId}`,
          `inventory/brands/company/${companyId}`,
          `product/brand/company/${companyId}`,
          `product/brands/company/${companyId}`,
          `company/${companyId}/brand`,
          `company/${companyId}/brands`,
        ];

        const categoryListCandidates = [
          `icategory/company/${companyId}`,
          `icategory/${companyId}`,
          `icategory?companyId=${companyId}`,
          `icategory`,
          `category/company/${companyId}`,
          `categories/company/${companyId}`,
          `inventory/category/company/${companyId}`,
          `inventory/categories/company/${companyId}`,
          `product/category/company/${companyId}`,
          `product/categories/company/${companyId}`,
          `company/${companyId}/category`,
          `company/${companyId}/categories`,
        ];

        let brands = [];
        let categories = [];
        let brandListPath = null;
        let categoryListPath = null;

        try {
          const r = await tryGetFirst(brandListCandidates);
          brandListPath = r.path;
          brands = r.res?.data?.data || r.res?.data?.data?.data || [];
        } catch (e) {
          brands = [];
        }

        try {
          const r = await tryGetFirst(categoryListCandidates);
          categoryListPath = r.path;
          categories = r.res?.data?.data || r.res?.data?.data?.data || [];
        } catch (e) {
          categories = [];
        }

        const nextBrandMap = {};
        (brands || []).forEach((b) => {
          const name =
            b?.brandName || b?.name || b?.BrandName || b?.title || "";
          const id = b?._id || b?.id || b?.brandId || b?.BrandId;
          const keyStrict = normalizeName(name);
          const keyLoose = normalizeLooseName(name);
          if (id) {
            if (keyStrict) nextBrandMap[keyStrict] = { id, name };
            if (keyLoose) nextBrandMap[keyLoose] = { id, name };
          }
        });

        const nextCategoryMap = {};
        (categories || []).forEach((c) => {
          const name =
            c?.categoryName || c?.name || c?.CategoryName || c?.title || "";
          const id = c?._id || c?.id || c?.categoryId || c?.CategoryId;
          const keyStrict = normalizeName(name);
          const keyLoose = normalizeLooseName(name);
          if (id) {
            if (keyStrict) nextCategoryMap[keyStrict] = { id, name };
            if (keyLoose) nextCategoryMap[keyLoose] = { id, name };
          }
        });

        if (!mounted) return;
        setBrandMap(nextBrandMap);
        setCategoryMap(nextCategoryMap);
        brandMapRef.current = nextBrandMap;
        categoryMapRef.current = nextCategoryMap;
        setApiPaths((prev) => ({
          ...prev,
          brandList: brandListPath,
          categoryList: categoryListPath,
        }));

        if (!brandListPath) {
          addLog({
            type: "warn",
            rowNumber: 0,
            message:
              "Brand list endpoint not detected (will rely on auto-create if enabled).",
          });
        }
        if (!categoryListPath) {
          addLog({
            type: "warn",
            rowNumber: 0,
            message:
              "Category list endpoint not detected (will rely on auto-create if enabled).",
          });
        }
      } catch (e) {
        // keep modal usable; auto-create can still work on demand
        console.log("Failed to fetch brand/category lists", e);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    fetchLists();
    return () => {
      mounted = false;
      mountedRef.current = false;
      cancelImportRef.current = true;
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    };
  }, [companyId]);

  // Auto-hide toast like other screens
  useEffect(() => {
    if (!success) return;
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    toastTimeoutRef.current = setTimeout(() => {
      if (!mountedRef.current) return;
      setSuccess(false);
      setEr("");
      setToastmsg("");
    }, er ? 5000 : 5000);
  }, [success, er]);

  const scheduleFlushLogs = () => {
    if (flushTimerRef.current) return;
    flushTimerRef.current = setTimeout(() => {
      flushTimerRef.current = null;
      if (!mountedRef.current) return;
      // Render only the last 200 logs to keep UI fast for large imports
      setLogs(logsRef.current.slice(-200));
    }, 100);
  };

  const addLog = (entry) => {
    logsRef.current.push({ ts: Date.now(), ...entry }); // { type, rowNumber, message }
    scheduleFlushLogs();
  };

  const scheduleFlushProgress = () => {
    if (progressFlushTimerRef.current) return;
    progressFlushTimerRef.current = setTimeout(() => {
      progressFlushTimerRef.current = null;
      if (!mountedRef.current) return;
      setProgress((p) => ({
        ...p,
        processed: countersRef.current.processed,
        success: countersRef.current.success,
        failed: countersRef.current.failed,
        skipped: countersRef.current.skipped,
      }));
    }, 120);
  };

  const bumpCounters = (delta) => {
    if (delta.processed) countersRef.current.processed += delta.processed;
    if (delta.success) countersRef.current.success += delta.success;
    if (delta.failed) countersRef.current.failed += delta.failed;
    if (delta.skipped) countersRef.current.skipped += delta.skipped;
    scheduleFlushProgress();
  };

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const apiPostWithRetry = async (path, payload, attempts = 4) => {
    let lastErr;
    for (let i = 0; i < attempts; i++) {
      try {
        // eslint-disable-next-line no-await-in-loop
        return await ApiPost(path, payload);
      } catch (e) {
        lastErr = e;
        const status = e?.response?.status;
        // Retry only on throttling / temporary backend issues / network
        const shouldRetry =
          status === 429 || status === 502 || status === 503 || status === 504 || !status;
        if (!shouldRetry) throw e;
        // eslint-disable-next-line no-await-in-loop
        await sleep(Math.min(1500, 250 * Math.pow(2, i)) + Math.floor(Math.random() * 120));
      }
    }
    throw lastErr;
  };

  const runPool = async (items, concurrency, handler) => {
    let idx = 0;
    const workers = new Array(Math.max(1, concurrency)).fill(null).map(async () => {
      while (idx < items.length) {
        if (cancelImportRef.current || !mountedRef.current) return;
        const currentIdx = idx++;
        // eslint-disable-next-line no-await-in-loop
        await handler(items[currentIdx], currentIdx);
      }
    });
    await Promise.all(workers);
  };

  const resetStateForNewFile = () => {
    setRows([]);
    setHeaderMap({});
    setDetectedHeaders([]);
    setLogs([]);
    logsRef.current = [];
    countersRef.current = { processed: 0, success: 0, failed: 0, skipped: 0 };
    setProgress({
      total: 0,
      processed: 0,
      success: 0,
      failed: 0,
      skipped: 0,
      running: false,
    });
  };

  const onPickFile = () => {
    if (fileRef.current) fileRef.current.click();
  };

  const onFileChange = async (e) => {
    const f = e?.target?.files?.[0];
    if (!f) return;
    resetStateForNewFile();
    setFileName(f.name);

    const ext = (f.name.split(".").pop() || "").toLowerCase();
    if (!["xls", "xlsx"].includes(ext)) {
      setSuccess(true);
      setToastmsg("Please upload a .xls or .xlsx file");
      setEr("error");
      return;
    }

    setIsLoading(true);
    try {
      const buf = await f.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheetName = wb.SheetNames?.[0];
      if (!sheetName) throw new Error("No sheet found in file");
      const ws = wb.Sheets[sheetName];
      const json = XLSX.utils.sheet_to_json(ws, {
        defval: "",
        raw: false,
      });

      const headers =
        json?.length > 0 ? Object.keys(json[0] || {}) : [];
      const hMap = buildHeaderMap(headers);

      setDetectedHeaders(headers);
      setHeaderMap(hMap);
      setRows(json);

      if (!hMap.productName || !hMap.brand || !hMap.category || !hMap.price || !hMap.quantity) {
        addLog({
          type: "error",
          rowNumber: 0,
          message:
            "Missing required columns. Required: Product Name, Brand, Category, Price, Quantity.",
        });
      }
      if ((json?.length || 0) > 500) {
        addLog({
          type: "warn",
          rowNumber: 0,
          message:
            "Large file detected. Recommended limit is 300–500 rows per import batch.",
        });
      }
    } catch (err) {
      setSuccess(true);
      setToastmsg(extractApiErrorMessage(err));
      setEr("error");
    } finally {
      setIsLoading(false);
    }
  };

  const resolveOrCreateBrand = async (brandName) => {
    const keyStrict = normalizeName(brandName);
    const keyLoose = normalizeLooseName(brandName);
    const existing =
      brandMapRef.current[keyStrict] || brandMapRef.current[keyLoose];
    if (existing?.id) return existing;
    if (!autoCreateMissing) throw new Error(`Brand not found: ${brandName}`);

    // In-flight lock (prevents duplicate creation in parallel imports)
    const lockKey = keyLoose || keyStrict;
    if (lockKey && brandInFlightRef.current[lockKey]) {
      return await brandInFlightRef.current[lockKey];
    }

    const canonicalName = canonicalizeDisplayName(brandName);
    // Send canonicalized name to reduce duplicates when backend is case-sensitive.
    const payload = { brandName: canonicalName, name: canonicalName, companyId };
    const candidates = [
      apiPaths.brandCreate,
      "ibrand",
      "brand",
      "brands",
      "inventory/brand",
      "inventory/brands",
      "product/brand",
      "product/brands",
    ].filter(Boolean);

    const creatorPromise = (async () => {
      let res;
      let lastErr;
      for (const p of candidates) {
        try {
          // eslint-disable-next-line no-await-in-loop
          res = await apiPostWithRetry(p, payload);
          setApiPaths((prev) => ({ ...prev, brandCreate: prev.brandCreate || p }));
          break;
        } catch (e) {
          lastErr = e;
        }
      }
      if (!res) throw lastErr || new Error("Brand create failed");

      const created = res?.data?.data || res?.data?.data?.data || res?.data;
      const id =
        created?._id ||
        created?.id ||
        created?.brandId ||
        created?.data?._id ||
        created?.data?.id;
      const name =
        created?.brandName ||
        created?.name ||
        created?.BrandName ||
        payload.brandName;

      const next = { id, name };
      brandMapRef.current = {
        ...brandMapRef.current,
        [keyStrict]: next,
        [keyLoose]: next,
      };
      setBrandMap(brandMapRef.current);
      return next;
    })();

    if (lockKey) brandInFlightRef.current[lockKey] = creatorPromise;
    try {
      return await creatorPromise;
    } finally {
      if (lockKey) delete brandInFlightRef.current[lockKey];
    }
  };

  const resolveOrCreateCategory = async (categoryName) => {
    const keyStrict = normalizeName(categoryName);
    const keyLoose = normalizeLooseName(categoryName);
    const existing =
      categoryMapRef.current[keyStrict] || categoryMapRef.current[keyLoose];
    if (existing?.id) return existing;
    if (!autoCreateMissing)
      throw new Error(`Category not found: ${categoryName}`);

    // In-flight lock (prevents duplicate creation in parallel imports)
    const lockKey = keyLoose || keyStrict;
    if (lockKey && categoryInFlightRef.current[lockKey]) {
      return await categoryInFlightRef.current[lockKey];
    }

    const canonicalName = canonicalizeDisplayName(categoryName);
    // Send canonicalized name to reduce duplicates when backend is case-sensitive.
    const payload = { categoryName: canonicalName, name: canonicalName, companyId };
    const candidates = [
      apiPaths.categoryCreate,
      "icategory",
      "category",
      "categories",
      "inventory/category",
      "inventory/categories",
      "product/category",
      "product/categories",
    ].filter(Boolean);

    const creatorPromise = (async () => {
      let res;
      let lastErr;
      for (const p of candidates) {
        try {
          // eslint-disable-next-line no-await-in-loop
          res = await apiPostWithRetry(p, payload);
          setApiPaths((prev) => ({
            ...prev,
            categoryCreate: prev.categoryCreate || p,
          }));
          break;
        } catch (e) {
          lastErr = e;
        }
      }
      if (!res) throw lastErr || new Error("Category create failed");

      const created = res?.data?.data || res?.data?.data?.data || res?.data;
      const id =
        created?._id ||
        created?.id ||
        created?.categoryId ||
        created?.data?._id ||
        created?.data?.id;
      const name =
        created?.categoryName ||
        created?.name ||
        created?.CategoryName ||
        payload.categoryName;

      const next = { id, name };
      categoryMapRef.current = {
        ...categoryMapRef.current,
        [keyStrict]: next,
        [keyLoose]: next,
      };
      setCategoryMap(categoryMapRef.current);
      return next;
    })();

    if (lockKey) categoryInFlightRef.current[lockKey] = creatorPromise;
    try {
      return await creatorPromise;
    } finally {
      if (lockKey) delete categoryInFlightRef.current[lockKey];
    }
  };

  const ensureStockInitialized = async ({
    productId,
    retailInitialStock,
    storeInitialStock,
  }) => {
    if (!productId) throw new Error("Missing productId for stock init");

    const retailStock = Number.isFinite(retailInitialStock)
      ? retailInitialStock
      : 0;
    const storeStock = Number.isFinite(storeInitialStock) ? storeInitialStock : 0;

    // Stock is not derived from /product in Salon Manager UI; it relies on a separate stock record.
    // Use the exact manual-flow endpoint/payload first; fallback to older guesses if needed.
    const postCandidates = [
      "stock/",
      "stock",
      "inventoryStock",
      "inventory/stock",
      "inventory/stock/init",
      "inventory/stock/initial",
      "product/stock",
      "productStock",
    ];

    // Manual flow:
    // POST /api/stock/
    // { productId, retailInitialStock, storeInitialStock, type: "CR", companyId }
    const payload = {
      productId,
      retailInitialStock: String(retailStock),
      storeInitialStock: String(storeStock),
      type: "CR",
      companyId,
    };

    let lastErr;
    for (const p of postCandidates) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await apiPostWithRetry(p, payload);
        return;
      } catch (e) {
        const status = e?.response?.status;
        // keep trying only when endpoint not found; otherwise surface real error
        if (status && status !== 404) throw e;
        lastErr = e;
      }
    }
    throw lastErr || new Error("Stock init endpoint not found");
  };

  const createProduct = async (rowData) => {
    // Match dev-api contract you shared for /api/product
    const retailPriceStr = String(rowData.price ?? "").trim();
    const quantityStr = String(rowData.quantityPerUnit ?? "").trim();
    const retailInitialStockStr = String(rowData.retailInitialStock ?? 0);
    const storeInitialStockStr = String(rowData.storeInitialStock ?? 0);

    const payload = {
      productType: rowData.productType || "Retail",
      unit: rowData.unit || undefined,
      taxType: "Inclusive",
      discountType: "₹",
      retailPrice: retailPriceStr,
      discount: "",
      discountPrice: retailPriceStr, // this drives "Final retail price" in UI
      productName: rowData.productName,
      quantity: quantityStr,
      retailInitialStock: retailInitialStockStr,
      storeInitialStock: storeInitialStockStr,
      hsn: rowData.hsn || "",
      categoryId: rowData.categoryId,
      brandId: rowData.brandId,
      tax: [],
      totalTax: 0,
      companyId,
      isActive: true,
      vendorId: [],
    };
    const candidates = [
      apiPaths.productCreate,
      "product",
      "products",
      "inventory/product",
      "inventory/products",
    ].filter(Boolean);

    let res;
    let lastErr;
    for (const p of candidates) {
      try {
        // eslint-disable-next-line no-await-in-loop
        res = await apiPostWithRetry(p, payload);
        setApiPaths((prev) => ({
          ...prev,
          productCreate: prev.productCreate || p,
        }));
        break;
      } catch (e) {
        lastErr = e;
      }
    }
    if (!res) throw lastErr || new Error("Product create failed");
    return res;
  };

  const startImport = async () => {
    if (!canImport) return;
    logsRef.current = [];
    setLogs([]);
    cancelImportRef.current = false;
    countersRef.current = { processed: 0, success: 0, failed: 0, skipped: 0 };
    setProgress({
      total: totalRows,
      processed: 0,
      success: 0,
      failed: 0,
      skipped: 0,
      running: true,
    });

    // Optional improvements:
    // - Pre-check brand/category casing/spacing mismatches and warn
    // - Skip duplicate rows within the same Excel file to avoid wasted API calls
    const normalizedToOriginals = (field) => {
      const m = new Map(); // norm -> Set(originals)
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const v = String(row?.[headerMap?.[field]] ?? "").trim();
        if (!v) continue;
        const k = normalizeName(v);
        if (!k) continue;
        if (!m.has(k)) m.set(k, new Set());
        m.get(k).add(v);
      }
      return m;
    };

    try {
      const brandVariants = normalizedToOriginals("brand");
      for (const [k, set] of brandVariants.entries()) {
        if (set.size > 1) {
          addLog({
            type: "warn",
            rowNumber: 0,
            message: `Brand name variants detected (treated as same): ${Array.from(set).join(
              " | "
            )}`,
          });
        }
      }
      const categoryVariants = normalizedToOriginals("category");
      for (const [k, set] of categoryVariants.entries()) {
        if (set.size > 1) {
          addLog({
            type: "warn",
            rowNumber: 0,
            message: `Category name variants detected (treated as same): ${Array.from(
              set
            ).join(" | ")}`,
          });
        }
      }
    } catch (e) {
      // non-blocking
    }

    const seenProductKeys = new Set(); // normalized final product name

    // Pre-check existing products (so re-importing the same Excel shows "Already exists" instead of success)
    const existingNameSet = new Set(); // normalized productName (best-effort)
    const existingCompositeSet = new Set(); // brandId|categoryId|normalizedName (best-effort)
    const getId = (v) => v?._id || v?.id || v;
    try {
      const productsRes = await ApiGet(`product/company/${companyId}`);
      const list = productsRes?.data?.data || [];
      for (const p of list) {
        const nameKey = normalizeName(p?.productName || "");
        if (nameKey) existingNameSet.add(nameKey);
        const bId = getId(p?.brandId);
        const cId = getId(p?.categoryId);
        if (bId && cId && nameKey) {
          existingCompositeSet.add(`${bId}|${cId}|${nameKey}`);
        }
      }
    } catch (e) {
      // If product list fetch fails, continue without pre-check.
    }

    const items = rows.map((row, i) => ({ row, excelRowNumber: i + 2 }));
    const CHUNK_SIZE = 50;
    // IMPORTANT: Parallel product creation can cause duplicate auto-generated productCode
    // if backend code generation is not atomic. Keep this at 1 to avoid race conditions.
    const CONCURRENCY = 1;

    for (let start = 0; start < items.length; start += CHUNK_SIZE) {
      if (cancelImportRef.current || !mountedRef.current) break;
      const chunk = items.slice(start, start + CHUNK_SIZE);

      // eslint-disable-next-line no-await-in-loop
      await runPool(chunk, CONCURRENCY, async ({ row, excelRowNumber }) => {
        if (cancelImportRef.current || !mountedRef.current) return;

        try {
          const { ok, errors, parsed } = validateRow(row, headerMap);
          if (!ok) {
            addLog({
              type: "skip",
              rowNumber: excelRowNumber,
              message: errors.join(", "),
            });
            bumpCounters({ skipped: 1, processed: 1 });
            return;
          }

          // Pre-check duplicates in the same file (based on final product name after prefixing)
          const candidateFinalName = computeFinalProductName({
            brandName: parsed.brand,
            productName: parsed.productName,
            prefixBrand: prefixBrandToName,
          });
          const dupKey = normalizeName(candidateFinalName);
          if (dupKey) {
            // Ensure we claim the key before any awaits to avoid duplicates under concurrency
            if (seenProductKeys.has(dupKey)) {
              addLog({
                type: "skip",
                rowNumber: excelRowNumber,
                message: `Duplicate in Excel file skipped: ${candidateFinalName}`,
              });
              bumpCounters({ skipped: 1, processed: 1 });
              return;
            }
            seenProductKeys.add(dupKey);
          }

          const brand = await resolveOrCreateBrand(parsed.brand);
          const category = await resolveOrCreateCategory(parsed.category);

          if (!brand?.id) throw new Error(`Brand create/resolve failed`);
          if (!category?.id) throw new Error(`Category create/resolve failed`);

          const finalName = computeFinalProductName({
            brandName: brand.name || parsed.brand,
            productName: parsed.productName,
            prefixBrand: prefixBrandToName,
          });

          // Check against already imported products (best-effort)
          const existingNameKey = normalizeName(finalName);
          const compositeKey = `${brand.id}|${category.id}|${existingNameKey}`;
          const exists =
            (existingNameKey && existingNameSet.has(existingNameKey)) ||
            (existingNameKey && existingCompositeSet.has(compositeKey));
          if (exists) {
            addLog({
              type: "skip",
              rowNumber: excelRowNumber,
              message: `Already exists, skipped: ${finalName}`,
            });
            bumpCounters({ skipped: 1, processed: 1 });
            return;
          }

          const createdRes = await createProduct({
            productName: finalName,
            brandId: brand.id,
            categoryId: category.id,
            price: parsed.price,
            quantityPerUnit: parsed.quantity,
            productType: parsed.productType,
            unit: parsed.unit,
            hsn: parsed.hsn,
            retailInitialStock: parsed.retailInitialStock,
            storeInitialStock: parsed.storeInitialStock,
          });

          // Best-effort stock initialization so Salon Manager doesn't show NaN.
          try {
            const createdProduct =
              createdRes?.data?.data ||
              createdRes?.data?.data?.data ||
              createdRes?.data ||
              {};
            const productId = createdProduct?._id || createdProduct?.id;
            await ensureStockInitialized({
              productId,
              retailInitialStock: parsed.retailInitialStock,
              storeInitialStock: parsed.storeInitialStock,
            });
          } catch (e) {
            // Don't fail the import if stock init is unavailable in an environment.
          }

          addLog({
            type: "success",
            rowNumber: excelRowNumber,
            message: `Created: ${finalName}`,
          });
          bumpCounters({ success: 1, processed: 1 });
        } catch (err) {
          addLog({
            type: "error",
            rowNumber: excelRowNumber,
            message: extractApiErrorMessage(err),
          });
          bumpCounters({ failed: 1, processed: 1 });
        }
      });

      // Yield between chunks to keep UI responsive and avoid request bursts
      // eslint-disable-next-line no-await-in-loop
      await sleep(150);
    }

    if (!mountedRef.current) return;
    scheduleFlushProgress();
    setProgress((p) => ({ ...p, running: false }));
    setSuccess(true);
    const successCount = countersRef.current.success;
    const failedCount = countersRef.current.failed;
    const skippedCount = countersRef.current.skipped;
    if (failedCount === 0 && skippedCount === 0) {
      setToastmsg("Import completed");
      setEr("");
      // Auto-close modal on fully successful import (keeps existing UX elsewhere unchanged)
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = setTimeout(() => {
        if (!mountedRef.current) return;
        toggle();
      }, 5000);
    } else {
      setToastmsg(
        `Import finished. Success: ${successCount}, Failed: ${failedCount}, Skipped: ${skippedCount}`
      );
      setEr(failedCount > 0 ? "error" : "");
    }
  };

  const percent =
    progress.total > 0 ? Math.round((progress.processed / progress.total) * 100) : 0;

  return (
    <>
      {isLoading && <Loader />}
      <div className="import-inventory-modal-backdrop" onClick={() => !progress.running && toggle()}>
        <div className="import-inventory-modal" onClick={(e) => e.stopPropagation()}>
          <div className="header">
            <h2>Import Inventory (Excel)</h2>
            <button className="close" onClick={() => !progress.running && toggle()}>
              ✕
            </button>
          </div>

          <div className="body">
            <div className="section">
              <div className="file-row">
                <input
                  ref={fileRef}
                  type="file"
                  accept=".xls,.xlsx"
                  style={{ display: "none" }}
                  onChange={onFileChange}
                />
                <button className="primary" onClick={onPickFile} disabled={progress.running}>
                  Select Excel file
                </button>
                <div className="file-name">
                  {fileName ? fileName : "No file selected"}
                </div>
              </div>

              <div className="toggles">
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={autoCreateMissing}
                    onChange={(e) => setAutoCreateMissing(e.target.checked)}
                    disabled={progress.running}
                  />
                  <span>Auto-create missing Brand & Category</span>
                </label>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={prefixBrandToName}
                    onChange={(e) => setPrefixBrandToName(e.target.checked)}
                    disabled={progress.running}
                  />
                  <span>Prefix Brand to Product Name</span>
                </label>
              </div>
            </div>

            <div className="section meta">
              <div className="meta-grid">
                <div className="meta-item">
                  <div className="label">Rows detected</div>
                  <div className="value">{totalRows}</div>
                </div>
                <div className="meta-item">
                  <div className="label">Progress</div>
                  <div className="value">
                    {progress.running
                      ? `${progress.processed}/${progress.total} (${percent}%)`
                      : progress.total
                      ? `${progress.total} ready`
                      : "-"}
                  </div>
                </div>
                <div className="meta-item">
                  <div className="label">Success / Failed / Skipped</div>
                  <div className="value">
                    {progress.success} / {progress.failed} / {progress.skipped}
                  </div>
                </div>
              </div>

              {progress.total > 0 && (
                <div className="progress-bar">
                  <div className="progress" style={{ width: `${percent}%` }} />
                </div>
              )}
            </div>

            {detectedHeaders?.length > 0 && (
              <div className="section">
                <div className="small-title">Detected columns (first sheet)</div>
                <div className="chips">
                  {detectedHeaders.map((h) => (
                    <span key={h} className="chip">
                      {h}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="section">
              <div className="small-title">Import log</div>
              <div className="log">
                {logs.length === 0 ? (
                  <div className="log-empty">No logs yet.</div>
                ) : (
                  logs.slice(-200).map((l) => (
                    <div key={`${l.ts}-${l.rowNumber}-${l.type}`} className={`log-row ${l.type}`}>
                      <div className="row-no">
                        {l.rowNumber ? `Row ${l.rowNumber}` : "File"}
                      </div>
                      <div className="msg">{l.message}</div>
                    </div>
                  ))
                )}
              </div>
              {logs.length > 200 && (
                <div className="hint">Showing last 200 log lines.</div>
              )}
            </div>
          </div>

          <div className="footer">
            <button className="secondary" onClick={() => !progress.running && toggle()}>
              Cancel
            </button>
            <button className="primary" onClick={startImport} disabled={!canImport}>
              {progress.running ? "Importing..." : "Start import"}
            </button>
          </div>
        </div>
      </div>

      {success && <Success modal={success} toastmsg={toastmsg} er={er} />}
    </>
  );
}


